import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
    FormBuilder,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
    UntilDestroy,
    untilDestroyed,
} from '@ngneat/until-destroy';
import { first } from 'rxjs';

import { MembersService } from '~/app/core/services/api/members/members.service';
import { AuthenticationFacade } from '~/app/core/state/authentication/authentication.facade';
import { ButtonWithIconComponent } from '~/app/shared/components/elements/buttons/button-with-icon/button-with-icon.component';
import { H2Component } from '~/app/shared/components/elements/h2/h2.component';
import { LabelComponent } from '~/app/shared/components/elements/label/label.component';
import { MaterialInputPhoneNumberComponent } from '~/app/shared/components/forms/formElements/material-input-phone-number/material-input-phone-number.component';
import { MaterialInputTextComponent } from '~/app/shared/components/forms/formElements/material-input-text/material-input-text.component';
import { SwitchInputComponent } from '~/app/shared/components/forms/formElements/switch-input/switch-input.component';
import { SnackbarService } from '~/app/shared/services/snackbar/snackbar.service';
import { MemberMePatchModel } from '~/app/shared/types/user/member-me-patch.model';
import { User } from '~/app/shared/types/user/user.type';
import { ProfilePhotoComponent } from '../profile-photo/profile-photo.component';

@UntilDestroy()
@Component({
    selector: 'eb-profile-professional-info',
    host: { '[class.profile-professional-info--embedded]': 'embedded()' },
    templateUrl: './profile-professional-info.component.html',
    styleUrls: ['./profile-professional-info.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ReactiveFormsModule,
        TranslocoPipe,
        H2Component,
        LabelComponent,
        ButtonWithIconComponent,
        MaterialInputTextComponent,
        MaterialInputPhoneNumberComponent,
        SwitchInputComponent,
        ProfilePhotoComponent,
    ],
})
export class ProfileProfessionalInfoComponent {
    private formBuilder = inject(FormBuilder);
    private snackbarService = inject(SnackbarService);
    private translocoService = inject(TranslocoService);
    private membersService = inject(MembersService);
    private authenticationFacade = inject(AuthenticationFacade);

    /**
     * EF-3921 — clé i18n du titre. `/profile` garde le titre par défaut ; la popin « profil incomplet »
     * (EF-3852) le surcharge pour dire POURQUOI on demande ces informations. Seul point de variation du
     * formulaire entre ses deux hôtes : sa logique (préremplissage, sauvegarde) reste commune.
     */
    public readonly titleKey = input('profile.professional_info.title');
    /** EF-3852 — phrase d'en-tête sous le titre. Renseignée par la popin de première connexion ; vide sur `/profile`. */
    public readonly subtitleKey = input<string | null>(null);
    /**
     * EF-3852 — intégré à la popin de première connexion : ni marge de carte ni boutons propres. La popin porte un seul
     * « Enregistrer » commun et appelle `saveChanges()`.
     */
    public readonly embedded = input(false);

    public readonly form = this.formBuilder.nonNullable.group({
        firstName: ['', [Validators.required]],
        lastName: ['', [Validators.required]],
        professionalEmail: ['', [Validators.required, Validators.email]],
        professionalPhone: [''],
        // EF-3831-2: registrationNumber + isCif désormais persistés (regulatoryRegistrationNumber / isCif).
        registrationNumber: [''],
        // `isCif` DOIT rester un booléen strict : `eb-switch-input` (non-neutre) mappe un `undefined`
        // sur « Oui » (voir SwitchInputComponent.externalToInternal). Défaut `false` = « Non ».
        isCif: [false],
    });

    /** Photo courante : `File` recadré, URL distante (lecture), ou `null`. */
    public readonly photo = signal<File | string | null>(null);

    public readonly photoHasChanged = signal(false);

    private readonly formValue = toSignal(this.form.valueChanges, { initialValue: this.form.getRawValue() });

    public readonly displayName = computed(() => {
        const value = this.formValue();
        return `${value.firstName ?? ''} ${value.lastName ?? ''}`.trim();
    });

    public readonly firstName = computed(() => this.formValue().firstName ?? '');

    public readonly lastName = computed(() => this.formValue().lastName ?? '');

    public readonly registrationNumber = computed(() => this.formValue().registrationNumber ?? '');

    private readonly formValid = signal(this.form.valid);

    /**
     * Valeurs enregistrées (référence). Comparer la valeur courante à cette référence est robuste aux
     * ré-émissions des CVA (ex. `eb-material-input-phone-number` re-marque le contrôle dirty au `writeValue`),
     * qui ne sont PAS de vraies modifications utilisateur. Sert aussi de base au reset (« Annuler »).
     */
    private readonly savedValue = signal(this.form.getRawValue());

    /** Photo enregistrée (référence pour le reset). */
    private readonly savedPhoto = signal<File | string | null>(null);

    /** Modifications réelles en attente (valeur ≠ référence enregistrée, ou photo), valides ou non. */
    public readonly hasUnsavedChanges = computed(() => (
        JSON.stringify(this.formValue()) !== JSON.stringify(this.savedValue()) || this.photoHasChanged()
    ));

    /** Actif uniquement si le formulaire est valide ET qu'il y a une modification (champ ou photo). */
    public readonly canSave = computed(() => this.formValid() && this.hasUnsavedChanges());

    /** Explication affichée en tooltip quand le bouton est désactivé. */
    public readonly disabledExplanationKey = computed(() => (
        this.formValid()
            ? 'common.error.no_changes'
            : 'profile.professional_info.required_fields_missing'
    ));

    constructor() {
        this.form.statusChanges
            .pipe(untilDestroyed(this))
            .subscribe(() => this.formValid.set(this.form.valid));

        this.loadCurrentMember();
    }

    public onPhotoChange(photo: File | null): void {
        this.photo.set(photo);
        this.photoHasChanged.set(true);
    }

    /** Restaure toutes les valeurs (champs + photo) au dernier état enregistré. */
    public cancelChanges(): void {
        this.form.reset(this.savedValue());
        this.form.markAsPristine();
        this.photo.set(this.savedPhoto());
        this.photoHasChanged.set(false);
    }

    public saveChanges(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        const value = this.form.getRawValue();
        const info: MemberMePatchModel = {
            firstName: value.firstName,
            lastName: value.lastName,
            email: value.professionalEmail,
            phone: value.professionalPhone,
            // EF-3831-2: back accepte désormais ces champs en écriture.
            isCif: value.isCif ?? false,
            regulatoryRegistrationNumber: value.registrationNumber ?? '',
        };

        // `undefined` → photoFileKey omis (photo inchangée) ; sinon File (upload) ou null (suppression).
        const photo = this.photoHasChanged() ? (this.photo() as File | null) : undefined;

        this.authenticationFacade.updateMemberInfo({ info, photo })
            .pipe(first(), untilDestroyed(this))
            .subscribe({
                next: () => {
                    this.form.markAsPristine();
                    this.savedValue.set(this.form.getRawValue());
                    this.savedPhoto.set(this.photo());
                    this.photoHasChanged.set(false);
                    this.snackbarService.openSuccess(
                        this.translocoService.translate('profile.changing_professional_info_success'),
                    );
                },
                error: () => {
                    this.snackbarService.openError(this.translocoService.translate('common.error_occurred'));
                },
            });
    }

    private loadCurrentMember(): void {
        const id = this.authenticationFacade.getUserSnapshot()?.id;
        if (!id) {
            return;
        }
        this.membersService.getMember(id)
            .pipe(first(), untilDestroyed(this))
            .subscribe((user: User) => this.prefill(user));
    }

    private prefill(user: User): void {
        // Données personnelles uniquement : le nom de société et l'adresse (données communes) sont
        // désormais gérés par la carte « Ma société » (`PATCH /companies`).
        this.form.patchValue({
            firstName: user.firstName ?? '',
            lastName: user.lastName ?? '',
            professionalEmail: user.email ?? '',
            professionalPhone: user.phone ?? '',
            registrationNumber: user.regulatoryRegistrationNumber ?? '',
            // `?? false` obligatoire : un `isCif` back absent/null ne doit pas devenir « Oui » au switch (cf. def du form).
            isCif: user.isCif ?? false,
        });
        this.photo.set(user.photoUrl ?? null);
        this.form.markAsPristine();
        this.savedValue.set(this.form.getRawValue());
        this.savedPhoto.set(user.photoUrl ?? null);
        this.photoHasChanged.set(false);
    }
}
